import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, filter, map, BehaviorSubject } from 'rxjs';
import { GridReadyEvent, ColDef } from 'ag-grid-community';
import { UserDetailsService } from '../../service/user-details.service';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import { UppNotification } from '../../model/notification';
import { NavigationService } from '../../service/core/navigation.service';
import { UppComponentNames } from '../../service/model';
import { PERMISSION_MANAGE_FARES } from '../../service/user-permissions.service';
import {
  ActivateFareRuleDisplayAction,
  ClearFareRulesAction,
  DeleteFareRuleAction,
  DeleteFareRuleDisplayNotificationAction,
  DeleteFareRuleSearchNotificationAction,
  SearchFareRuleAction,
  StartFareModificationAction
} from '../../store/fares/fares-action';
import {
  selectFareRuleDeleteSending,
  selectFareRules,
  selectFareRuleSearchNotification,
  selectFareRuleSearchSending
} from '../../store/fares/fares-selector';
import { FareRuleDeleteRequest, FareRuleSearchRequest } from '../model/fare-request';
import { FareSearchCriteriaUi } from '../model/fare-rule-search-criteria';
import { FareRuleUi } from '../model/fare-rule-ui';
import { displayableFields, displayableFieldsWithId } from './fare-search-displayable-fields';
import { FaresSearchCriteriaComponent } from './fares-search-criteria/fares-search-criteria.component';
import { convertToFareSearchRequest } from '../model/fare-rule-converter';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { FaresState } from '../../store/fares/fares-state';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../../core/util/resources';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { AgGridAngular } from 'ag-grid-angular';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { CopyToClipboardButtonComponent } from '../../components/copy-to-clipboard-button/copy-to-clipboard-button.component';

interface FareSearchResult {
  id: string;
  name: string;
  description: string;
  version: number;
  organization?: string;
  pointOfSaleNames: string;
  originNames: string;
  destinationNames: string;
  ruleStatusActive: boolean;
}

@Component({
  selector: 'ama-ng-upp-fares-search',
  templateUrl: './fares-search.component.html',
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    FaresSearchCriteriaComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class FaresSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(FaresSearchCriteriaComponent)
  searchCriteriaComponent!: FaresSearchCriteriaComponent;

  fareRuleSearchSending$: Observable<boolean>;
  fareRuleDeleteSending$: Observable<boolean>;
  fareRules$: Observable<FareRuleUi[]>;
  transformedFareRules$: Observable<FareSearchResult[]>;
  notification$: Observable<UppNotification>;

  mainMessages: NotificationMessages;
  managePermission = PERMISSION_MANAGE_FARES;
  displayableFields: DisplayableField[] = displayableFieldsWithId;

  troubleShootingEnabled = false;
  agGridTablesActivated = false;
  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);

  subscription: Subscription = new Subscription();

  constructor(
    store: Store<FaresState>,
    router: Router,
    navigationService: NavigationService,
    private readonly userDetailsService: UserDetailsService,
    private readonly configurationService: ConfigurationService
  ) {
    super(store, router, navigationService);

    this.fareRuleSearchSending$ = this.store.pipe(select(selectFareRuleSearchSending));
    this.fareRuleDeleteSending$ = this.store.pipe(
      select(selectFareRuleDeleteSending),
      map((value) => Boolean(value))
    );
    this.fareRules$ = this.store.pipe(select(selectFareRules));
    this.transformedFareRules$ = this.fareRules$.pipe(
      map((fareRules) =>
        (fareRules || []).filter((fr) => fr && !fr.statusType).map((fr) => this.recordToSearchResult(fr))
      )
    );
    this.notification$ = this.store.pipe(
      select(selectFareRuleSearchNotification),
      filter((notification): notification is UppNotification => notification !== undefined)
    );
    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.troubleshootingPanel).subscribe((isEnabled) => {
        this.troubleShootingEnabled = isEnabled ?? false;
      })
    );

    this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
      this.agGridTablesActivated = isEnabled ?? false;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.displayableFields = this.troubleShootingEnabled ? displayableFieldsWithId : displayableFields;
    this.navigationService.setSelectedMenuTitle($localize`:@@upp.fares.search.navigationTitle:Fare Search`);
    this.navigationService.activateFares();
    this.navigationService.enableNavigation();

    if (this.agGridTablesActivated) {
      this.initializeColumnDefs();
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  searchFareRules(searchCriteria: FareSearchCriteriaUi) {
    const request = this.createFareRulesSearchRequest(searchCriteria);
    request.rule = this.userDetailsService.assignRuleEntity(request.rule);
    this.store.dispatch(new SearchFareRuleAction({ request }));
  }

  closeNotification() {
    this.store.dispatch(new DeleteFareRuleSearchNotificationAction({}));
  }

  clearFareRules() {
    this.store.dispatch(new ClearFareRulesAction({}));
    this.router.navigate(['fares/search']);
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.FARES;
  }

  getEntryType(): EntryType {
    return EntryType.FARE;
  }

  recordToSearchResult(fareRecord: FareRuleUi): FareSearchResult {
    const fareRecordForSearch: FareSearchResult = {
      id: fareRecord.id ?? '',
      name: fareRecord.rule?.name ?? '',
      description: fareRecord.rule?.description ?? '',
      version: Number.parseInt(fareRecord.rule?.version ?? '0'),
      organization: fareRecord.rule?.organization ?? '',
      pointOfSaleNames: fareRecord.reference?.pointOfSaleNames?.toString() ?? '',
      originNames: fareRecord.reference?.originMarkets?.toString() ?? '',
      destinationNames: fareRecord.reference?.destinationMarkets?.toString() ?? '',
      ruleStatusActive: fareRecord.rule?.active ?? false
    };

    return fareRecordForSearch;
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): FareRuleDeleteRequest {
    return {
      version,
      fare: {
        partition: organization,
        ruleType: 'DYCTEX',
        release: 0,
        persistentId: id,
        ruleVersion: ruleVersion.toString()
      }
    };
  }

  entryToUpdateNameResolver(entryToUpdate: FareRuleDeleteRequest) {
    return entryToUpdate.fare.persistentId;
  }

  showFareRule(id: string) {
    this.store.dispatch(new ActivateFareRuleDisplayAction({ id: undefined }));
    this.store.dispatch(new DeleteFareRuleDisplayNotificationAction({}));
    this.router.navigate(['fares/display/' + id]);
  }

  modifyFareRule(id: string) {
    this.store.dispatch(new StartFareModificationAction({ id }));
    this.router.navigate(['fares/modify/']);
  }

  deleteFareRule(request: FareRuleDeleteRequest) {
    this.store.dispatch(new DeleteFareRuleAction({ request }));
  }

  storeUnsavedFareSearch() {
    this.searchCriteriaComponent.storeUnsavedFareSearch();
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => this.showFareRule((data as FareSearchResult).id ?? '');
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyFareRule((data as FareSearchResult).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return (_data: unknown) => {};
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const fare = data as FareSearchResult;
      const deleteRequest = this.entryToUpdateCreator(
        fare.id ?? '',
        fare.name ?? '',
        fare.organization ?? '',
        '1',
        fare?.version ?? 1
      );
      this.deleteFareRule(deleteRequest);
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return () => {};
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.fareRuleDeleteSending$.pipe(map((value) => value ?? false));
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null): void {
    this.activeConfirmationRowId$.next(rowId);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  private createFareRulesSearchRequest(searchCriteriaUi: FareSearchCriteriaUi): FareRuleSearchRequest {
    return convertToFareSearchRequest(searchCriteriaUi);
  }

  private initializeColumnDefs(): void {
    const dataColumns = this.displayableFields
      .filter((field) => field.value !== 'description')
      .map((field) => {
        const colDef: ColDef = {
          field: field.value,
          headerName: field.label,
          sortable: field.isSortable ?? false,
          unSortIcon: field.isSortable ?? false,
          resizable: false,
          suppressMovable: true
        };

        if (field.hasCopyToClipboard) {
          colDef.cellRenderer = CopyToClipboardButtonComponent;
          colDef.cellRendererParams = {
            showIdWithButton: true
          };
          colDef.minWidth = 200;
        }

        if (field.value === 'id' || field.value === 'name') {
          colDef.sortable = true;
        }

        if (field.value === 'ruleStatusActive') {
          colDef.valueGetter = (params) => (params.data?.ruleStatusActive ? 'Active' : 'Inactive');
        }

        return colDef;
      });

    this.columnDefs = [
      ...dataColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        flex: 2,
        minWidth: 200,
        wrapText: true,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          copyForbidden: true
        }
      }
    ];
  }
}
